import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import PrimaryButton from 'components/buttons/PrimaryButton';
import DateNavigationButton from 'components/buttons/DateNavigationButton';
import IconButton from 'components/buttons/IconButton';
import { generateDisplayDays, navigatePeriod } from './functions/schedulingDateUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox, faMapLocationDot, faRotateRight, faWrench } from '@fortawesome/free-solid-svg-icons';
import { Period, ResourceGroupType, SchedulerMode, SchedulingView } from './SchedulingTypes';
import TabsButton from 'components/buttons/TabsButton';
import JobModal from '../../views/jobs/JobModal';
import MultiSelectButton from 'components/buttons/MultiSelectButton';
import CustomizeSchedulingModal from './CustomizeSchedulingModal';
import AddJobOrActivityForm from './AddJobOrActivityForm';
import { useSettings } from 'services/settings/SettingsContext';
import { UserType } from '../../views/users/UserTypes';
import SelectButton from 'components/buttons/SelectButton';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useActiveFeature, usePackageFeature } from 'services/permissions/permissionChecks';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { faTablePivot } from '@fortawesome/pro-solid-svg-icons';

interface SchedulingNavigationProps {
    days: Date[];
    view: SchedulingView;
    loading: string;
    selectableResourceGroups: ResourceGroupType[];
    selectedResourceGroups: number[];
    selectedUser: number | null;
    users: UserType[];
    onViewChange: (view: SchedulingView) => void;
    onSetDays: (days: Date[]) => void;
    onSetSelectedResourceGroups: (selectedResourceGroups: number[]) => void;
    onSetSelectedUser: (userId: number) => void;
    onRefresh: () => void;
}

const SchedulingNavigation: React.FC<SchedulingNavigationProps> = ({ 
    days, view, selectableResourceGroups, selectedResourceGroups, loading,
    selectedUser, users, onViewChange, onSetDays, onSetSelectedResourceGroups, 
    onSetSelectedUser, onRefresh
 }) => {
    const { t } = useTranslation();
    const { environmentType } = useAuthContext();
    const { environmentSettings } = useSettings();
    const { initializeModal } = useModal();
    const [newJobId, setNewJobId] = useState<number | null>(null);
    const showProjectScheduling = environmentSettings?.project_show_scheduling;
    const hasEmployeeSchedulerPackageFeature = usePackageFeature('scheduling_timeline_views');
    const hasEmployeeSchedulerActiveFeature = useActiveFeature('scheduling_timeline_views');
    const hasEmployeeScheduler = hasEmployeeSchedulerPackageFeature && hasEmployeeSchedulerActiveFeature;
    const hasMultipleGroups = selectableResourceGroups?.length > 1;
    const hasMapSchedulerPackageFeature = usePackageFeature('map_scheduler');
    const hasMapSchedulerActiveFeature = useActiveFeature('map_scheduler');
    const hasMapSchedulerFeature = hasMapSchedulerPackageFeature && hasMapSchedulerActiveFeature;
    const hasJobInboxPackageFeature = usePackageFeature('job_inbox');
    const hasJobInboxActiveFeature = useActiveFeature('job_inbox');
    const hasJobInboxFeature = hasJobInboxPackageFeature && hasJobInboxActiveFeature;

    // Determine which side panel is currently open, only one can be active
    const isJobInboxActive = view.showJobInbox && !view.showSchedulerMap;
    const isSchedulerMapActive = view.showJobInbox && view.showSchedulerMap;

    // Open the job inbox in the side panel, or close the panel when it is already open
    const handleToggleJobInbox = () => {
        onViewChange({ ...view, showJobInbox: !isJobInboxActive, showSchedulerMap: false });
    };

    // Open the map in the side panel, or close the panel when it is already open
    const handleToggleSchedulerMap = () => {
        onViewChange({ ...view, showJobInbox: !isSchedulerMapActive, showSchedulerMap: !isSchedulerMapActive });
    };

    // Open the newly added job if the id is set
    useEffect(() => {
        if (!newJobId) return;

        initializeModal(
            <JobModal itemId={newJobId} />, {
                itemId: newJobId,
                modalSize: 'large'
            }
        );

        setNewJobId(null);
    }, [newJobId]);

    // Handle scheduler mode change
    const handleSchedulerModeChange = (value: 'team' | 'projects') => {
        if (value === 'projects') {
            onViewChange({
                ...view,
                mode: 'projects',
                viewType: 'project_planner',
                period: 'week',
                timeDisplayMode: 'none'
            });
        }

        if (value === 'team') {
            onViewChange({
                ...view,
                mode: 'team',
                viewType: 'employee_timeline',
                timeDisplayMode: 'timeslots'
            });
        }
    };

    // Handle date navigation and fetch corresponding appointments
    const handleNavigate = (direction: 'prev' | 'next' | 'date', date?: Date) => {
        let updatedView = view;

        // Handle next / prev buttons
        if (direction === 'prev' || direction === 'next') {
            updatedView = navigatePeriod(view, direction);

        // Handle date selection
        } else if (direction === 'date' && date) {
            updatedView = { ...view, displayDate: date };
        }

        // If the view is changed, update the states
        if (updatedView !== view) {
            
            // Update the view
            onViewChange(updatedView);
            
            // Generate new days for the updated view
            const newDays = generateDisplayDays(updatedView);
            onSetDays(newDays);
        }
    };

    // Handle navigating to today
    const handleGoToToday = () => {
        // let updatedView = view;

        // Get todays date
        const today = new Date();

        const updatedView = { ...view, displayDate: today }

        // if (view.period === 'week') {
        //     updatedView = { ...view, displayDate: today };
        // }

        // if (view.period === 'day') {
        //     updatedView = {
        //         ...view,
        //         displayDate: today,
        //     };
        // }

        // Update view
        onViewChange(updatedView);

        // Generate new days for the updated view
        const newDays = generateDisplayDays(updatedView);
        onSetDays(newDays);
    };

    // Handle the day or week period change
    const handlePeriodChange = (newPeriod: Period) => {

        // Handle day view selection
        if (newPeriod === "day") {

            // Determine of today falls in the current week
            const today = new Date();
            const todayInCurrentWeek = days.some(d =>
                d.getFullYear() === today.getFullYear() &&
                d.getMonth() === today.getMonth() &&
                d.getDate() === today.getDate()
            );

            // If today falls in the current week, go to today
            onViewChange({
                ...view,
                period: "day" as Period,
                displayDate: todayInCurrentWeek ? today : view.displayDate,
            });
        }

        // Handle week view selection
        if (newPeriod === "week") {
            onViewChange({
                ...view,
                period: "week" as Period,
            });
        }
    };

    // Handle create new job or activity from the scheduling header
    const handleCreateNewJob = async () => {
        initializeModal(
            React.cloneElement(<AddJobOrActivityForm />), {
                title: t('scheduling.other_activity.add_activity_label'),
                modalSize: 'add-form',
                onSuccess: (response: any) => {
                    // Open the job modal only for a created job, an activity response has no job items
                    if (response?.id && Array.isArray(response?.items)) {
                        setNewJobId(response.id);
                    }

                    // Trigger scheduler event refresh
                    onRefresh();
                }
            } 
        );
    };

    // Render the view switch
    const renderViewSwitch = () => {
        return (
            <SelectButton
                label="scheduling.scheduling_header.view_label"
                size="small"
                items={[
                    { label: 'scheduling.scheduling_header.calendar_view_label', value: 'personal_calendar' },
                    { label: 'scheduling.scheduling_header.timeline_view_label', value: 'employee_timeline' },
                    { label: 'scheduling.scheduling_header.list_view_label', value: 'employee_list' },
                ]}
                selectedItem={view.viewType}
                onSelectionChange={(selected) => {
                    if (typeof selected !== 'string') return;

                    onViewChange({
                        ...view,
                        viewType: selected as SchedulingView['viewType'],
                        period: selected === 'personal_calendar' ? 'week' : view.period,
                        timeDisplayMode: selected === 'employee_list' ? 'none' : 'timeslots',
                    });
                }}
                openOnHover
            />
        )
    };

    // Handle direction toggle
    const handleToggleDirection = () => {
        onViewChange({
            ...view,
            direction: view.direction === 'stacked' ? 'columns' : 'stacked',
        });
    };

    // Sort the users to be shown in the employee select button
    const sortedUsers = users.filter(user => user.is_active && user.field_access).sort((a, b) => {

        // Set the priority for the users
        const getPriority = (user: UserType) => {

            // Show admins on top
            if (user.is_admin) {
                return 0;
            }

            // Then users with both office and field access
            if (user.office_access && user.field_access) {
                return 1;
            }

            // At last users only with field access
            if (user.field_access) {
                return 2;
            }

            return 3;
        };

        // Get the priority
        const priorityA = getPriority(a);
        const priorityB = getPriority(b);

        if (priorityA !== priorityB) {
            return priorityA - priorityB;
        }

        // Sort alphabetically
        return a.full_name!.localeCompare(b.full_name!);
    });

    // Render the personal calendar navigation
    const personalCalendarNavigation = () => {
        return (
            <div className='container scheduling-header'>
                <div className='content-left'>
                    {view.createJobs && (
                        <PrimaryButton
                            onClick={() => handleCreateNewJob()}
                            onlyViewRestriction={true}
                            label={t('button.plus_add_object_label', { object_name: t(`job.general.object_name.singular`) })}
                            size="small"
                        />
                    )}
                </div>
                <div className='content-right button-container'>
                    {loading === 'show-loader' && (
                        <div className="loader"></div>
                    )}

                    {users.filter(user => user.is_active && user.field_access).length > 1 && (
                        <SelectButton
                            label="scheduling.scheduling_header.employee_label"
                            size="small"
                            items={sortedUsers.map(user => ({
                                label: String(user.full_name),
                                value: user.id,
                            }))}
                            selectedItem={selectedUser}
                            onSelectionChange={(userId) => onSetSelectedUser(Number(userId))}
                            openOnHover
                        />
                    )}

                    {hasEmployeeScheduler && (
                        renderViewSwitch()
                    )}

                    <IconButton
                        onClick={() => initializeModal(React.cloneElement(
                            <CustomizeSchedulingModal 
                                view={view} 
                                onViewChange={onViewChange}
                            />
                        ), { modalSize: 'small-medium' })}
                        icon={<FontAwesomeIcon icon={faWrench} />}
                        tooltipText='scheduling.scheduling_header.customize_button_tooltip'
                        size="small" 
                    />
                    <DateNavigationButton
                        currentDate={days} 
                        isDayView={view.period === 'day'}
                        onNavigate={handleNavigate}
                    />
                    {showProjectScheduling && (
                        <TabsButton
                            buttons={[
                                { label: 'scheduling.scheduling_header.team_mode_label', tabValue: 'team' },
                                { label: 'scheduling.scheduling_header.project_mode_label', tabValue: 'projects' },
                            ]}
                            currentTabValue={view.viewType === 'project_planner' ? 'projects' : 'team'}
                            onTabsClick={(value) => handleSchedulerModeChange(value as SchedulerMode)}
                            size="small"
                        />
                    )}
                </div>
            </div>
        )
    };

    // Render the employee scheduler navigation
    const employeeSchedulerNavigation = () => {
        return (
            <div className='container scheduling-header'>
                <div className='content-left'>
                    {view.createJobs && (
                        <PrimaryButton
                            onClick={() => handleCreateNewJob()}
                            onlyViewRestriction={true}
                            label={t('button.plus_add_object_label', { object_name: t(`job.general.object_name.singular`) })}
                            size="small"
                        />
                    )}
                </div>
                <div className='content-right button-container'>
                    {loading === 'show-loader' && (
                        <div className="loader"></div>
                    )}

                    <SecondaryButton
                        onClick={handleGoToToday}
                        label="scheduling.scheduling_header.today_label"
                        size="small"
                    />

                    <TabsButton
                        buttons={[
                            { label: 'scheduling.scheduling_header.day_label', tabValue: 'day' },
                            { label: 'scheduling.scheduling_header.week_label', tabValue: 'week' },
                        ]}
                        currentTabValue={view.period}
                        onTabsClick={(value) => handlePeriodChange(value as Period)}
                        size="small"
                    />

                    {hasEmployeeScheduler && (
                        renderViewSwitch()
                    )}

                    {environmentType === 'trial' && (
                        <IconButton
                            onClick={handleToggleDirection}
                            icon={<FontAwesomeIcon icon={faTablePivot} />}
                            tooltipText="scheduling.scheduling_header.rotate_view"
                            size="small"
                        />
                    )}

                    {hasMultipleGroups && (
                        <MultiSelectButton
                            label="scheduling.scheduling_header.groups_label"
                            size="small"
                            items={selectableResourceGroups.map((resourceGroup: ResourceGroupType) => ({
                                label: resourceGroup.name,
                                value: resourceGroup.id as number,
                            }))}
                            selectedItems={selectedResourceGroups}
                            onSelectionChange={(selectedIds) => onSetSelectedResourceGroups(selectedIds)}
                        />
                    )}
                    <IconButton
                        onClick={() => initializeModal(React.cloneElement(
                            <CustomizeSchedulingModal 
                                view={view} 
                                onViewChange={onViewChange}
                            />
                        ), { modalSize: 'small-medium' })}
                        icon={<FontAwesomeIcon icon={faWrench} />}
                        tooltipText='scheduling.scheduling_header.customize_button_tooltip'
                        size="small" 
                    />
                    <DateNavigationButton
                        currentDate={days} 
                        isDayView={view.period === 'day'}
                        onNavigate={handleNavigate}
                    />
                    <div className='side-panel-buttons'>
                        {hasJobInboxFeature && (
                            <IconButton
                                onClick={handleToggleJobInbox}
                                icon={<FontAwesomeIcon icon={faInbox} />}
                                label='scheduling.scheduling_header.job_inbox_label'
                                isActive={isJobInboxActive}
                                size="small"
                            />
                        )}
                        {hasMapSchedulerFeature && (
                            <IconButton
                                onClick={handleToggleSchedulerMap}
                                icon={<FontAwesomeIcon icon={faMapLocationDot} />}
                                label='scheduling.scheduling_header.scheduler_map_label'
                                isActive={isSchedulerMapActive}
                                size="small"
                            />
                        )}
                    </div>
                    {showProjectScheduling && (
                        <TabsButton
                            buttons={[
                                { label: 'scheduling.scheduling_header.team_mode_label', tabValue: 'team' },
                                { label: 'scheduling.scheduling_header.project_mode_label', tabValue: 'projects' },
                            ]}
                            currentTabValue={view.viewType === 'project_planner' ? 'projects' : 'team'}
                            onTabsClick={(value) => handleSchedulerModeChange(value as SchedulerMode)}
                            size="small"
                        />
                    )}
                </div>
            </div>
        )
    };

    // Render the project scheduler navigation
    const projectSchedulerNavigation = () => {
        return (
            <div className='container scheduling-header'>
                <div className='content-left' />
                <div className='content-right button-container'>
                    {loading === 'show-loader' && (
                        <div className="loader"></div>
                    )}
                    <IconButton
                        onClick={onRefresh}
                        icon={<FontAwesomeIcon icon={faRotateRight} />}
                        tooltipText='scheduling.scheduling_header.refresh_tooltip'
                        size="small"
                    />
                    <DateNavigationButton
                        currentDate={days} 
                        isDayView={view.period === 'day'}
                        onNavigate={handleNavigate}
                    />
                    <TabsButton
                        buttons={[
                            { label: 'scheduling.scheduling_header.team_mode_label', tabValue: 'team' },
                            { label: 'scheduling.scheduling_header.project_mode_label', tabValue: 'projects' },
                        ]}
                        currentTabValue={view.viewType === 'project_planner' ? 'projects' : 'team'}
                        onTabsClick={(value) => handleSchedulerModeChange(value as SchedulerMode)}
                        size="small"
                    />
                </div>
            </div>
        )
    };

    return (
        <>
            {view.viewType === 'personal_calendar' && (
                personalCalendarNavigation()
            )}

            {(view.viewType === 'employee_list' || view.viewType === "employee_timeline") && (
                employeeSchedulerNavigation()
            )}
            
            {view.viewType === 'project_planner' && (
                projectSchedulerNavigation()
            )}
        </>
    )
}

export default SchedulingNavigation;